"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import type { Brief } from "@/lib/onboarding/brief";
import type { StreamEvent } from "@/lib/onboarding/streamEvents";

/** A turn in the conversation. Assistant `content` is raw (includes the
 *  trailing `<<META …>>` line); the UI strips/parses it for display. */
export interface ChatMessage {
  readonly role: "user" | "assistant";
  content: string;
}

/**
 * Conversation status:
 * - idle: waiting for the visitor's next message
 * - streaming: the assistant's reply is arriving
 * - done: the assistant submitted the brief; conversation complete
 * - busy / error: transient failures the visitor can retry
 * - unavailable: backend is down — show the email fallback
 */
export type ChatStatus =
  | "idle"
  | "streaming"
  | "done"
  | "busy"
  | "error"
  | "unavailable";

export interface Prefill {
  readonly packageInterest?: string;
  readonly careInterest?: string;
}

interface PersistedState {
  messages: ChatMessage[];
  done: boolean;
  brief: Brief | null;
}

const STORAGE_KEY = "cs-onboarding-v1";

/**
 * Drives the onboarding chat: owns the message list, POSTs each turn to
 * `/api/start/chat`, parses the newline-delimited JSON stream into the growing
 * assistant message, captures the submitted brief, and persists the
 * conversation to localStorage so a refresh resumes where it left off.
 *
 * The localized greeting is rendered by the UI and is intentionally NOT part of
 * `messages` (the model conversation must start with the visitor's first turn).
 */
export function useAssistantChat(prefill: Prefill) {
  const locale = useLocale();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [status, setStatus] = useState<ChatStatus>("idle");
  const [brief, setBrief] = useState<Brief | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const hydrated = useRef(false);

  // Restore a prior conversation on mount (client-only). setState-in-effect is
  // the correct pattern here: reading localStorage during render would cause an
  // SSR/client hydration mismatch, so restoration must happen post-hydration.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved) as PersistedState;
        messagesRef.current = state.messages;
        /* eslint-disable react-hooks/set-state-in-effect */
        setMessages(state.messages);
        setBrief(state.brief);
        if (state.done) setStatus("done");
        /* eslint-enable react-hooks/set-state-in-effect */
      }
    } catch {
      /* ignore corrupt storage */
    }
    hydrated.current = true;
  }, []);

  // Persist after every settled change (skip until hydration to avoid clobber).
  useEffect(() => {
    if (!hydrated.current) return;
    if (status === "streaming") return;
    try {
      const state: PersistedState = {
        messages,
        done: status === "done",
        brief,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* ignore quota errors */
    }
  }, [messages, status, brief]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || status === "streaming" || status === "done") return;

      const outgoing: ChatMessage[] = [
        ...messagesRef.current,
        { role: "user", content: trimmed },
      ];
      const withAssistant: ChatMessage[] = [
        ...outgoing,
        { role: "assistant", content: "" },
      ];
      messagesRef.current = withAssistant;
      setMessages(withAssistant);
      setStatus("streaming");

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch("/api/start/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locale, messages: outgoing, prefill }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          messagesRef.current = outgoing;
          setMessages(outgoing);
          setStatus(
            res.status === 503
              ? "unavailable"
              : res.status === 429
                ? "busy"
                : "error",
          );
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let finished: ChatStatus = "idle";

        const appendDelta = (delta: string) => {
          const next = [...messagesRef.current];
          const last = next[next.length - 1];
          next[next.length - 1] = { ...last, content: last.content + delta };
          messagesRef.current = next;
          setMessages(next);
        };

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const raw of lines) {
            if (!raw.trim()) continue;
            const evt = JSON.parse(raw) as StreamEvent;
            if (evt.type === "text") appendDelta(evt.value);
            else if (evt.type === "brief") {
              if (evt.brief) setBrief(evt.brief);
              finished = "done";
            } else if (evt.type === "error") finished = "error";
          }
        }
        setStatus(finished);
      } catch (err) {
        // A user-initiated Stop aborts the fetch; keep the partial reply.
        if (err instanceof DOMException && err.name === "AbortError") {
          setStatus("idle");
          return;
        }
        messagesRef.current = outgoing;
        setMessages(outgoing);
        setStatus("error");
      } finally {
        abortRef.current = null;
      }
    },
    [locale, prefill, status],
  );

  /** Interrupt a streaming reply, keeping whatever has arrived so far. */
  const stop = useCallback(() => abortRef.current?.abort(), []);

  /** Clear the conversation and start fresh. */
  const reset = useCallback(() => {
    abortRef.current?.abort();
    messagesRef.current = [];
    setMessages([]);
    setBrief(null);
    setStatus("idle");
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  return { messages, status, brief, send, stop, reset };
}
